<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentPauseTemplatesAPI from 'dashboard/api/agentPauseTemplates';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
  agentName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();

const templates = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const selectedTemplateIds = ref([]);

const fetchTemplates = async () => {
  isFetching.value = true;

  try {
    const response = await AgentPauseTemplatesAPI.get(props.agentId);
    templates.value = response.data;
    selectedTemplateIds.value = response.data
      .filter(template => template.assigned_directly)
      .map(template => template.id);
  } catch (error) {
    useAlert(t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const saveTemplates = async () => {
  isSaving.value = true;

  try {
    await AgentPauseTemplatesAPI.update(
      props.agentId,
      selectedTemplateIds.value
    );
    useAlert(t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.API.UPDATE_SUCCESS'));
    emit('updated');
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.API.UPDATE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="flex flex-col h-auto max-h-[85vh] overflow-auto">
    <woot-modal-header
      :header-title="
        $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.TITLE', { agentName: agentName })
      "
      :header-content="$t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.DESCRIPTION')"
    />

    <div v-if="isFetching" class="py-8 text-center text-sm text-n-slate-11">
      {{ $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.LOADING') }}
    </div>

    <div
      v-else-if="!templates.length"
      class="py-8 text-center text-sm text-n-slate-11"
    >
      {{ $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-3 p-1">
      <label
        v-for="template in templates"
        :key="template.id"
        class="flex items-start gap-3 rounded-xl border border-n-weak p-4"
      >
        <input
          v-model="selectedTemplateIds"
          :value="template.id"
          type="checkbox"
        />
        <div class="flex-1">
          <div class="text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </div>
          <div class="mt-2 flex flex-wrap gap-2">
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              {{
                $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.LIST.DURATION', {
                  count: template.duration_in_minutes,
                })
              }}
            </span>
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              {{
                $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.LIST.DAILY_LIMIT', {
                  count: template.daily_limit,
                })
              }}
            </span>
            <span
              v-if="template.assigned_via_team"
              class="rounded-full bg-n-brand-2 px-2 py-1 text-xs text-n-brand-11"
            >
              {{ $t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.LIST.INHERITED') }}
            </span>
          </div>
        </div>
      </label>

      <div class="flex justify-end">
        <Button
          :label="$t('AGENT_MGMT.AGENT_PAUSE_TEMPLATES.SAVE')"
          :is-loading="isSaving"
          @click="saveTemplates"
        />
      </div>
    </div>
  </div>
</template>
